import { useCallback, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { usePieceDispatch } from '../../model/piece-context';
import { useUploadMedia } from '../upload-media-provider';
import type { AudioLayer } from '../../model/types';

const readAudioDuration = (url: string): Promise<number> =>
  new Promise<number>((resolve, reject) => {
    const el = document.createElement('audio');
    el.preload = 'metadata';
    el.src = url;
    el.addEventListener('loadedmetadata', () => {
      if (isFinite(el.duration) && el.duration > 0) resolve(el.duration);
      else reject(new Error('Audio metadata missing duration'));
    });
    el.addEventListener('error', () => reject(new Error('Audio failed to load')));
  });

/**
 * Uploads an audio file as a new audio layer in the main model.
 *
 * When `uploadMedia` is available (via UploadMediaProvider), the audio is
 * uploaded to R2 in the background and the layer's src is updated from
 * the blob URL to the permanent public URL once the upload completes.
 */
export const useAudioUpload = () => {
  const inputRef = useRef<HTMLInputElement>(null);
  const dispatch = usePieceDispatch();
  const uploadMedia = useUploadMedia();

  const open = useCallback(() => inputRef.current?.click(), []);

  const handleChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;
      event.target.value = '';

      const blobUrl = URL.createObjectURL(file);
      let durationSec: number;
      try {
        durationSec = await readAudioDuration(blobUrl);
      } catch {
        URL.revokeObjectURL(blobUrl);
        return;
      }

      const layerId = uuidv4();
      const layer: AudioLayer = {
        id: layerId,
        kind: 'audio',
        visible: true,
        locked: false,
        label: file.name,
        start: 0,
        duration: Math.round(durationSec * 1000),
        fit: 'fixed',
        content: { src: blobUrl }
      };
      dispatch({ type: 'addAudioLayer', layer });

      // Background upload: swap blob → permanent URL
      if (uploadMedia) {
        uploadMedia(file, 'audio').then(({ publicUrl }) => {
          dispatch({ type: 'updateAudioSrc', id: layerId, src: publicUrl });
          URL.revokeObjectURL(blobUrl);
        }).catch(() => {
          // Upload failed — keep the blob URL.
        });
      }
    },
    [dispatch, uploadMedia]
  );

  return { open, inputRef, handleChange };
};
